import { TasksAPI } from "@/api/tasks";
import { EXTERNAL_NOTIFICATION_EVENTS_MAP } from "@/misc/externalNotificationEvents";
import { Selectors as UserSelectors } from "@/store/user/selectors";
import { Effects as ExternalNotificationsEffects } from "@/store/externalNotifications/effects";
import { analyticsAction } from "@/utils/analytics";
import pick from "lodash/pick";
import isempty from "lodash/isEmpty";
import merge from "lodash/merge";
import { TaskMutationHandler } from "./mutationHandler";
import websocket from "@/websocket";
import { getReduxStore } from "@/store";

const createTask = async (data, { extraTasksPartialData, isCopy } = {}) => {
  const { data: task } = await TasksAPI.createTask(data);

  const tasks = [task];
  if (!isempty(extraTasksPartialData)) {
    for (const partialData of extraTasksPartialData) {
      const { data: task } = await TasksAPI.createTask({
        ...data,
        ...partialData,
      });
      tasks.push(task);
    }
  }

  websocket.execIfNotConnected(() => {
    TaskMutationHandler.multipleTasksCreated({ tasks });
  });

  const store = getReduxStore();
  const authMasterId = UserSelectors.getMasterId(store.getState());
  if (!isCopy && task.client && task.master === authMasterId) {
    store.dispatch(
      ExternalNotificationsEffects.promptForNotification({
        task: task._id,
        event: EXTERNAL_NOTIFICATION_EVENTS_MAP.MASTER_CREATE_TASK,
      })
    );
  }
  if (!isCopy) {
    if (UserSelectors.isStandardMode(store.getState())) {
      analyticsAction({
        category: "tasks",
        action: "personal_task_todo",
      });
    } else if (task.personal) {
      analyticsAction({
        category: "tasks",
        action: "personal_task_pro",
      });
    }
  }
  return task;
};

export const TasksService = {
  createTask,
  async copyTask(baseTask) {
    const data = {
      ...pick(baseTask, [
        "start_date",
        "end_date",
        "notify",
        "personal",
        "cal_color",
        "location",
        "location_hr",
        "master",
      ]),
      custom_fields: (baseTask.custom_fields || []).map(({ key, value }) => ({
        key,
        value,
      })),
    };
    if (baseTask.personal) {
      merge(data, pick(baseTask, ["title", "personal_photos", "master_note"]));
    } else {
      merge(data, {
        custom: true,
        ...pick(baseTask, [
          "location_type",
          "prepayment",
          "client",
          "price",
          "hide_client",
          "auto_notifications",
        ]),
        note: baseTask.master_note || "",
        saloon: baseTask.saloon || undefined,
        services: baseTask.services.map((s) => ({
          amount: s.amount,
          service: s.service,
          discounts: [s.discount].filter(Boolean),
        })),
      });
    }
    const copiedTask = await createTask(data, { isCopy: true });
    return copiedTask;
  },
  async updateTask(id, updates, oldTask) {
    const { data: task } = await TasksAPI.updateById(id, updates);

    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskUpdated({ updated_task: task, task: oldTask });
    });

    const store = getReduxStore();
    const authMasterId = UserSelectors.getMasterId(store.getState());
    const isTaskMasterAndWithClient =
      task.client && task.master === authMasterId;
    const startDate = oldTask.start_date;
    const oldClient = oldTask.client;

    // TODO:: if master has removed the client
    // should we prompt for the "cancel" notification?
    if (
      isTaskMasterAndWithClient &&
      task.status !== "DONE" &&
      task.status !== "CANCELLED"
    ) {
      // master has changed a client, or added the client to the task without a client
      if (!oldClient || task.client !== oldClient) {
        store.dispatch(
          ExternalNotificationsEffects.promptForNotification({
            task: task._id,
            event: EXTERNAL_NOTIFICATION_EVENTS_MAP.MASTER_CREATE_TASK,
          })
        );
      } else if (startDate && startDate !== task.start_date) {
        store.dispatch(
          ExternalNotificationsEffects.promptForNotification({
            task: task._id,
            event: EXTERNAL_NOTIFICATION_EVENTS_MAP.MASTER_CHANGE_TASK,
          })
        );
      }
    }

    return task;
  },
  async finishTask(task) {
    await TasksAPI.finishTask(task._id);
    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskCompleted({ task });
    });
  },
  async cancelTask(task, data) {
    await TasksAPI.cancelTask(task._id, data);

    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskCancelled({ task });
    });

    const store = getReduxStore();
    const authMasterId = UserSelectors.getMasterId(store.getState());
    if (task.client && task.master === authMasterId) {
      store.dispatch(
        ExternalNotificationsEffects.promptForNotification({
          task: task._id,
          event: EXTERNAL_NOTIFICATION_EVENTS_MAP.MASTER_CANCEL_TASK,
        })
      );
    }
  },
  async removeTask(task) {
    await TasksAPI.removeTask(task._id);

    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskRemoved({ task });
    });
  },
  async acceptTask(initialTask) {
    const { data: task } = await TasksAPI.acceptTask(initialTask._id);

    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskAccepted({ task });
    });

    const store = getReduxStore();
    const authMasterId = UserSelectors.getMasterId(store.getState());
    if (task.master === authMasterId) {
      store.dispatch(
        ExternalNotificationsEffects.promptForNotification({
          task: task._id,
          event: EXTERNAL_NOTIFICATION_EVENTS_MAP.MASTER_ACCEPT_TASK,
        })
      );
    }
  },
  async requestPhoto(task) {
    const taskId = task._id;

    const {
      data: { updates },
    } = await TasksAPI.requestPhoto(taskId);

    if (updates) {
      websocket.execIfNotConnected(() => {
        TaskMutationHandler.taskPhotoRequested({ task_id: taskId, updates });
      });
    }
  },
  async requestReview(task) {
    const {
      data: { task: updatedTask },
    } = await TasksAPI.requestReview(task._id);
    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskReviewRequested({ task: updatedTask });
    });
  },
  async requestPayout(taskId, data) {
    const {
      data: { updates },
    } = await TasksAPI.requestPayout(taskId, data);
    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskPartiallyUpdated({
        task_id: taskId,
        updates,
      });
    });
  },
  async createNote(task, payload) {
    const taskId = task._id;
    const {
      data: { updates },
    } = await TasksAPI.createNote(taskId, payload);
    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskNoteUpdated({ task_id: taskId, updates });
    });
  },
  async removeNote(task) {
    const taskId = task._id;
    const {
      data: { updates },
    } = await TasksAPI.createNote(taskId, { text: "" });
    websocket.execIfNotConnected(() => {
      TaskMutationHandler.taskNoteUpdated({ task_id: taskId, updates });
    });
  },
};
